import React, { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { equativSyncApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

// deepcontext-9fn6: Frame 1 of the locked design (page 14/18). The page
// title + subtitle come from AppLayout's per-page header (pageTitles.ts's
// /admin/taxonomy entry) — no in-page <h1> here, the shell owns page
// identity. Both exports are peer actions, so they're outline accent pills
// rather than a gradient primary (gradient is reserved for a page's single
// primary action).
const TaxonomyExport: React.FC = () => {
    const [exportingType, setExportingType] = useState<string | null>(null);
    const [delimiter, setDelimiter] = useState('|');

    const handleExport = async (type: 'eskimi_only' | 'public') => {
        setExportingType(type);
        try {
            await equativSyncApi.exportTaxonomy(type, 'production', delimiter);
        } catch (error) {
            console.error(`Failed to export ${type} taxonomy:`, error);
        } finally {
            setExportingType(null);
        }
    };

    return (
        // -mt-[21px] aligns the card's top edge with the offset the other
        // pages' toolbars land on (deepcontext-jlkv mechanism,
        // deepcontext-fbj1.1 — mirrors SearchFilterBar.tsx's Blueprints
        // toolbar).
        <Card className="-mt-[21px] p-4 max-w-xl">
            <div className="flex items-end gap-3.5">
                <div className="w-16">
                    <label htmlFor="delimiter" className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1">
                        Delimiter
                    </label>
                    <input
                        id="delimiter"
                        type="text"
                        value={delimiter}
                        onChange={(e) => setDelimiter(e.target.value)}
                        maxLength={3}
                        className="h-[42px] w-full rounded-full border border-line bg-transparent px-3 text-xs text-center font-mono text-ink focus:border-accent focus:ring-1 focus:ring-accent"
                    />
                </div>
                <ExportButton
                    label="Export Eskimi Only"
                    loadingLabel="Exporting…"
                    isLoading={exportingType === 'eskimi_only'}
                    disabled={exportingType !== null || !delimiter}
                    onClick={() => handleExport('eskimi_only')}
                />
                <ExportButton
                    label="Export Public"
                    loadingLabel="Exporting…"
                    isLoading={exportingType === 'public'}
                    disabled={exportingType !== null || !delimiter}
                    onClick={() => handleExport('public')}
                />
            </div>
        </Card>
    );
};

interface ExportButtonProps {
    label: string;
    loadingLabel: string;
    isLoading: boolean;
    disabled: boolean;
    onClick: () => void;
}

const ExportButton: React.FC<ExportButtonProps> = ({
    label, loadingLabel, isLoading, disabled, onClick,
}) => (
    <Button
        variant="outline"
        onClick={onClick}
        disabled={disabled}
        className="whitespace-nowrap"
    >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        {isLoading ? loadingLabel : label}
    </Button>
);

export default TaxonomyExport;
